import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const CATEGORIES = [
  "Private Label Purified Water",
  "Private Label Alkaline Water",
  "Private Label Spring Water",
  "Private Label Sparkling Water",
  "Private Label CBD Water",
  "Private Label CBD Water for Pets",
];

export const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [stages, setStages] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", client_id: "", product_category: CATEGORIES[0], target_launch: "", notes: "" });

  const load = () => {
    api.get("/admin/projects").then((r) => setProjects(r.data.projects)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data.clients)).catch(() => {});
    api.get("/pipeline/stages").then((r) => setStages(r.data.stages)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/projects", form);
      toast.success("Project created");
      setShowNew(false);
      setForm({ name: "", client_id: "", product_category: CATEGORIES[0], target_launch: "", notes: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateStage = async (project, stage) => {
    try {
      await api.patch(`/admin/projects/${project.id}`, { stage });
      toast.success(`Project moved to ${stage}`);
      load();
      if (selected?.id === project.id) setSelected({ ...selected, stage });
    } catch { toast.error("Update failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Projects</div>
          <h1 className="text-3xl font-bold tracking-tight">Beverage project pipeline.</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="ft-btn-primary" data-testid="new-project-btn"><Plus size={16} /> New Project</button>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="admin-projects-table">
          <thead><tr><th>Project</th><th>Client</th><th>Category</th><th>Stage</th><th>Progress</th></tr></thead>
          <tbody>
            {projects.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--ft-muted)] py-10">No projects yet.</td></tr>}
            {projects.map((p) => {
              const client = clients.find((c) => c.id === p.client_id);
              return (
                <tr key={p.id} className="cursor-pointer" onClick={() => setSelected(p)} data-testid={`project-row-${p.id}`}>
                  <td className="font-semibold">{p.name}</td>
                  <td>{client ? client.full_name : "—"}</td>
                  <td className="text-sm">{p.product_category}</td>
                  <td><span className="ft-badge">{p.stage}</span></td>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-1.5 bg-[var(--ft-border)] rounded-full overflow-hidden">
                        <div className="h-full" style={{ width: `${p.progress}%`, background: "var(--ft-blue)" }} />
                      </div>
                      <span className="text-xs text-[var(--ft-muted)]">{p.progress}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-2xl font-bold">New Project</h2>
              <button onClick={() => setShowNew(false)} className="text-[var(--ft-muted)]"><X size={20} /></button>
            </div>
            <form onSubmit={create} className="space-y-4" data-testid="new-project-form">
              <div>
                <label className="ft-label">Project Name*</label>
                <input required className="ft-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="proj-name" />
              </div>
              <div>
                <label className="ft-label">Client*</label>
                <select required className="ft-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="proj-client">
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.company || c.email}</option>)}
                </select>
              </div>
              <div>
                <label className="ft-label">Product Category*</label>
                <select required className="ft-input" value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="ft-label">Target Launch</label>
                <input className="ft-input" placeholder="e.g. Q3 2026" value={form.target_launch} onChange={(e) => setForm({ ...form, target_launch: e.target.value })} />
              </div>
              <div>
                <label className="ft-label">Notes</label>
                <textarea rows={3} className="ft-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <button type="submit" className="ft-btn-primary w-full justify-center" data-testid="proj-create">Create Project</button>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="project-detail-modal">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="ft-eyebrow">Project</div>
                <h2 className="text-2xl font-bold mt-2">{selected.name}</h2>
                <div className="text-sm text-[var(--ft-muted)]">{selected.product_category}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={20} /></button>
            </div>
            <div className="ft-label mt-4">Move to Stage</div>
            <div className="flex flex-wrap gap-2">
              {stages.map((s) => (
                <button
                  key={s}
                  onClick={() => updateStage(selected, s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    selected.stage === s ? "bg-[var(--ft-blue)] text-white border-[var(--ft-blue)]" : "border-[var(--ft-border)] hover:border-[var(--ft-blue)]"
                  }`}
                  data-testid={`stage-${s}`}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {}); }, []);
  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Products</div>
        <h1 className="text-3xl font-bold tracking-tight">Product catalog.</h1>
        <p className="text-[var(--ft-muted)] mt-2">Beverage categories currently in production. Extended catalog roadmap includes protein, energy, functional, coffee, tea, and RTD formats.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {products.map((p) => (
          <div key={p.slug} className="ft-card">
            <div className="ft-badge">{p.min_order}</div>
            <h2 className="mt-4 text-xl font-semibold">{p.name}</h2>
            <p className="text-sm text-[var(--ft-muted)] mt-2">{p.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.bottle_options.map((b) => (
                <span key={b} className="text-xs px-2 py-1 rounded-full border border-[var(--ft-border)]">{b}</span>
              ))}
            </div>
            <div className="mt-4 text-xs text-[var(--ft-muted)]">Timeline: <span className="text-[var(--ft-charcoal)] font-semibold">{p.timeline}</span></div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};
